﻿import React from "react";
import "./SidenavMiniMap.scss";
import Canvas from "../Canvas/Canvas";
import { Canvas as CanvasModel } from "../../store/storyline/types";
import { CircularProgress, ConditionalTreeItem } from "../../shared/components";
import { Book, Chapter, VDT } from "../../shared/components/icons";
import { SimpleTreeView as TreeView, TreeItem } from "@mui/x-tree-view";
import { useAppSelector } from "../../store";
import { useAppActions } from "../../shared/hooks";
import { Tooltip as ReactTooltip } from "react-tooltip";
import * as _ from "lodash";
import { TriangleDown, TriangleRight } from "../../shared/components/icons";

interface MiniMapFramePreviewProps {
    slide: CanvasModel;
    ix: number;
    iy: number;
    fi: number;
};

const MiniMapFramePreview: React.FC<MiniMapFramePreviewProps> = React.memo(function MiniMapFramePreview({ slide, ix, iy, fi }) {
    const template = slide?.template;
    const frameData = slide?.data;

    if (!template || !frameData) return null;

    return (
        <div className="minimap-hover-tooltip-content">
            <div className="hover-tooltip-slide-title">{frameData?.["title"]}</div>
            <div className="slide">
                <Canvas key={`mini-map-slide-hover-preview-${ix}-${iy}-${fi}`} template={template} templateName={slide.templateName} frameData={frameData} staticPlot={true} allowSideEffects={false} />
            </div>
        </div>
    );
});

function SidenavMiniMap() {
    const pageError = useAppSelector(state => state.app.errors.page);
    const canvases = useAppSelector(state => state.storyline?.canvases);
    const loading = useAppSelector(state => state.storyline?.loading);
    const { goToXYZ } = useAppActions();
    const [expandedNodes, setExpandedNodes] = React.useState([]);
    const hideMiniMap = canvases?.length <= 1 && canvases?.[0]?.length <= 1 && canvases?.[0]?.[0]?.length <= 1;

    React.useEffect(() => {
        const allIds = _.flatten(canvases.map((c, ci) => [`node-${ci}`, ...c.map((p, pi) => `node-${ci}-${pi}`)]));
        setExpandedNodes(allIds);
    }, [canvases]);

    const onExpandedItemsChange = (event: any, nodeIds: string[]) => {
        setExpandedNodes(nodeIds);
    }

    const getTooltipContent = React.useCallback(({ content: dataString }) => {
        if (!dataString) return null;
        const [ix, iy, fi] = dataString.split("|").map(a => parseInt(a));
        return <MiniMapFramePreview slide={canvases?.[ix]?.[iy]?.[fi]} ix={ix} iy={iy} fi={fi} />;
    }, [canvases]);

    if (pageError == null && loading) {
        return <div className="minimap-loading"><CircularProgress /></div>;
    }

    return (
        hideMiniMap ?
            null :
            <div className="minimap">
            <span>
                <ReactTooltip id="slide-preview" variant="light" place="right" delayShow={0} delayHide={0} clickable={false} className="tooltip-primary minimap-hover-tooltip" render={getTooltipContent} />
            </span>
                <TreeView
                    expandedItems={expandedNodes}
                    onExpandedItemsChange={onExpandedItemsChange}
                    disableSelection={true}
                    slots={{
                        expandIcon: (params) => <TriangleDown {...params} size="small" />,
                        collapseIcon: (params) => <TriangleRight {...params} size="small" />
                    }}
                >
                    {
                        canvases.map((chapter, chapterIndex) => <ConditionalTreeItem key={`node-${chapterIndex}`} show={chapter.length > 1} itemId={`node-${chapterIndex}`} className={chapterIndex === chapterIndex ? "active" : ""} label={
                            <div className="menu-item-label">
                                <Book size="small" />
                                <span>
                                    {
                                        `Chapter ${chapterIndex + 1}`
                                    }
                                </span>
                            </div>
                        }>
                            {chapter.map((page, pageIndex) => <ConditionalTreeItem key={`node-${chapterIndex}-${pageIndex}`} show={page.length > 1} itemId={`node-${chapterIndex}-${pageIndex}`} className={chapterIndex === chapterIndex && pageIndex === pageIndex ? "active" : ""} label={
                                <div className="menu-item-label">
                                    <Chapter size="small" />
                                    <span>
                                        {
                                            `Page ${pageIndex + 1}`
                                        }
                                    </span>
                                </div>
                            }>
                                {
                                    page.map((paragraph, paragraphIndex) =>
                                        <TreeItem key={`node-${chapterIndex}-${pageIndex}-${paragraphIndex}`} itemId={`node-${chapterIndex}-${pageIndex}-${paragraphIndex}`} className={chapterIndex === chapterIndex && pageIndex === pageIndex && paragraphIndex === paragraphIndex ? "active tooltip-target" : "tooltip-target"} label={
                                            <div className="menu-item-label">
                                                <VDT size="small" />
                                                <span>{paragraph["title"]}</span>
                                            </div>
                                        }
                                            data-tooltip-id="slide-preview"
                                            data-tooltip-content={`${chapterIndex}|${pageIndex}|${paragraphIndex}`}
                                            onClick={() => goToXYZ(chapterIndex, pageIndex, paragraphIndex)}
                                        >
                                        </TreeItem>)}
                            </ConditionalTreeItem>)}
                        </ConditionalTreeItem>)
                    }
                </TreeView>
            </div>
    );
}

export default React.memo(SidenavMiniMap);